import type { SoftphoneCallStatusProps } from './index.types';
import { ANALYTICS_EVENT_SOFTPHONE } from 'Modules/softphone/constants/analytics';
import { useCallTimer } from 'Modules/softphone/hooks';
import { useConferenceCallManager } from 'Modules/softphone/hooks/useConferenceCallManager';
import { useConferenceTimer } from 'Modules/softphone/hooks/useConferenceTimer';
import {
  getCallStateFlags,
  getCallStateLabel,
} from 'Modules/softphone/resolver';
import { useSoftphoneStore } from 'Modules/softphone/store';
import type { CallState } from 'Modules/softphone/types';
import React from 'react';
import { pushToGTMDataLayer } from 'Utils/analytics';
import { formatDuration, formatDurationForSpeech } from 'Utils/formatDuration';
import { CallStatusIndicator } from './CallStatusIndicator';
import { callStatusStyles } from './index.styles';

/**
 * SoftphoneCallStatus - Displays call status or timer
 *
 * Shows either:
 * - Call status text (e.g., "Calling...", "Ringing...") when call is not connected
 * - Call duration timer (MM:SS or HH:MM:SS) when call is connected
 * - Timer with status indicator (orange dot + "On Hold") when call is on hold
 *
 * All state is derived from the store - no props needed except testID.
 */
export const SoftphoneCallStatus = ({
  testID,
}: SoftphoneCallStatusProps): React.ReactElement => {
  const callStatusTestId = testID ? `${testID}-call-status` : undefined;
  const currentCall = useSoftphoneStore((state) =>
    state.currentCallId
      ? state.activeCalls.get(state.currentCallId) ?? null
      : null
  );

  const { isConferenceActive, conference } = useConferenceCallManager();

  // Check if the current call is part of the conference.
  // When a new 1:1 call is active during a conference, show the call timer, not the conference timer.
  const showConferenceView =
    isConferenceActive && Boolean(currentCall?.conferenceId);

  // Get call timer
  const { duration: callDuration } = useCallTimer();

  // Conference timer — calculate duration from conference.startTime
  const conferenceDuration = useConferenceTimer(
    conference?.startTime,
    showConferenceView
  );

  const duration = showConferenceView ? conferenceDuration : callDuration;

  const callState: CallState = currentCall?.state ?? 'idle';
  const flags = getCallStateFlags(callState);
  const status = getCallStateLabel(callState);
  const isConnected = showConferenceView || flags.isConnected;
  const isOnHold = !showConferenceView && (currentCall?.isOnHold ?? false);

  const { baseStyle, textStyle } = callStatusStyles;

  const durationLabel = showConferenceView
    ? 'Conference duration'
    : 'Call duration';

  return (
    // Part of the identity group in SoftphoneOnCallView, so no role of its own,
    // and not a live region: everything worth speaking goes through the
    // announcer, the only place no-duplicates and latest-wins are enforced.
    <div className={baseStyle} data-testid={callStatusTestId}>
      {isConnected ? (
        <div className="flex items-center gap-2">
          {/* aria-live="off": the timer ticks every second, never announce it */}
          {/*
            Focus is the only half of "the user navigated here" the page can
            see; a reader's browse cursor fires nothing. Under-reports, never
            over-reports.
          */}
          <time
            className={textStyle}
            aria-live="off"
            aria-atomic="true"
            dateTime={`PT${duration}S`}
            onFocus={() =>
              pushToGTMDataLayer(
                ANALYTICS_EVENT_SOFTPHONE.SR_DURATION_ANNOUNCED,
                { seconds: duration, scope: showConferenceView ? 'conference' : 'call' }
              )
            }
          >
            <span aria-hidden="true">{formatDuration(duration)}</span>
            <span className="sr-only">
              {`${durationLabel} ${formatDurationForSpeech(duration)}`}
            </span>
          </time>
          {isOnHold && (
            <CallStatusIndicator
              status="held"
              testID={testID ? `${testID}-status-indicator` : undefined}
            />
          )}
        </div>
      ) : (
        <span className={textStyle}>{status}</span>
      )}
    </div>
  );
};
